/**
 * AiSetAssistantDialog — one-shot AI worship set suggester.
 * Calls `POST /admin/worship/assistant/suggest`, shows N picks with
 * reasoning, and lets the user add any subset to the current set.
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Sparkles, PlusCircle, Wand2 } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const MODELS = [
  { key: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', hint: 'fast · cheap' },
  { key: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', hint: 'nuanced' },
  { key: 'gpt-5.4-mini', label: 'GPT 5.4 Mini', hint: 'balanced' },
];

export default function AiSetAssistantDialog({
  currentSongIds = [], onClose, onAddSong, churchId,
}) {
  const [theme, setTheme] = useState('');
  const [duration, setDuration] = useState(25);
  const [currentKey, setCurrentKey] = useState('');
  const [tone, setTone] = useState('reflective');
  const [count, setCount] = useState(4);
  const [model, setModel] = useState(MODELS[0].key);
  const [result, setResult] = useState(null);

  const suggest = useMutation({
    mutationFn: async () => (await axios.post(
      `${API}/api/admin/worship/assistant/suggest`,
      {
        church_id: churchId || null,
        current_song_ids: currentSongIds,
        theme: theme || null,
        target_duration_min: Number(duration) || null,
        current_key: currentKey || null,
        tone: tone || null,
        count: Number(count) || 4,
        model,
      },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: (d) => { setResult(d); toast.success(`${d.suggestions.length} picks ready`); },
    onError: (e) => toast.error(`AI: ${e?.response?.data?.detail || e.message}`),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="ai-assistant-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-fuchsia-300" /> AI Worship Set Assistant
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-xs">
          <p className="text-slate-400">
            Suggest {count} songs from your library based on theme, key flow, and last-used gaps.
            Picks only come from songs you already have — click <em>Add</em> on any pick to append it.
          </p>

          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Theme</label>
              <Input data-testid="ai-theme" value={theme} onChange={(e) => setTheme(e.target.value)}
                     placeholder="grace · thanksgiving · call to worship"
                     className="mt-1 bg-slate-950 border-slate-700 h-8 text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Target duration (min)</label>
              <Input data-testid="ai-duration" type="number" value={duration} onChange={(e) => setDuration(e.target.value)}
                     className="mt-1 bg-slate-950 border-slate-700 h-8 text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Current end-key</label>
              <Input data-testid="ai-key" value={currentKey} onChange={(e) => setCurrentKey(e.target.value)}
                     placeholder="G · D · Bb"
                     className="mt-1 bg-slate-950 border-slate-700 h-8 text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Tone</label>
              <select data-testid="ai-tone" value={tone} onChange={(e) => setTone(e.target.value)}
                      className="mt-1 h-8 w-full rounded border border-slate-700 bg-slate-950 px-2 text-xs">
                <option value="reflective">Reflective</option>
                <option value="upbeat">Upbeat</option>
                <option value="balanced">Balanced</option>
                <option value="contemplative">Contemplative</option>
                <option value="celebratory">Celebratory</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">How many picks</label>
              <Input data-testid="ai-count" type="number" min={1} max={6} value={count} onChange={(e) => setCount(e.target.value)}
                     className="mt-1 bg-slate-950 border-slate-700 h-8 text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Model</label>
              <select data-testid="ai-model" value={model} onChange={(e) => setModel(e.target.value)}
                      className="mt-1 h-8 w-full rounded border border-slate-700 bg-slate-950 px-2 text-xs">
                {MODELS.map((m) => (
                  <option key={m.key} value={m.key}>{m.label} ({m.hint})</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-500">
              Current set: {currentSongIds.length} song{currentSongIds.length === 1 ? '' : 's'}
            </span>
            <Button data-testid="ai-suggest" onClick={() => suggest.mutate()} disabled={suggest.isPending}
                    className="bg-fuchsia-600 hover:bg-fuchsia-500">
              <Wand2 className="mr-1 h-3 w-3" /> {suggest.isPending ? 'Thinking…' : 'Suggest songs'}
            </Button>
          </div>

          {result && (
            <div className="space-y-2 rounded border border-slate-800 bg-slate-950 p-3" data-testid="ai-results">
              {result.narrative && (
                <p className="italic text-slate-300">{result.narrative}</p>
              )}
              <div className="space-y-1">
                {result.suggestions.map((s) => (
                  <div key={s.song_id} data-testid={`ai-pick-${s.song_id}`}
                       className="flex items-center justify-between rounded border border-slate-800 bg-slate-900/70 px-2 py-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-100">{s.title}</span>
                        {s.key && <Badge variant="outline" className="border-slate-700 text-[9px] text-slate-400">key {s.key}</Badge>}
                        {(s.themes || []).slice(0, 2).map((t) => (
                          <Badge key={t} variant="outline" className="border-violet-500/30 text-violet-300 text-[9px]">{t}</Badge>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-0.5">{s.reason}</p>
                      <p className="text-[9px] text-slate-500">
                        used {s.usage_count}× · last {s.last_used_at ? new Date(s.last_used_at).toLocaleDateString() : 'never'}
                      </p>
                    </div>
                    {onAddSong && (
                      <Button size="sm" data-testid={`ai-add-${s.song_id}`}
                              className="bg-emerald-600 hover:bg-emerald-500"
                              onClick={() => { onAddSong(s); toast.success(`Added ${s.title}`); }}>
                        <PlusCircle className="mr-1 h-3 w-3" /> Add
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[9px] text-slate-500">Model: {result.model}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
